import { Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useSentryUser } from "@/hooks/useSentryUser";
import { useSupabasePerformance } from "@/hooks/useSupabasePerformance";
import { useAuthInitialization } from "@/hooks/useAuthInitialization";
import { usePageTracking } from "@/hooks/usePageTracking";
import { WebVitalsReporter } from "@/components/analytics/WebVitalsReporter";
import { CLSReporter } from "@/components/analytics/CLSReporter";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { MobileBottomNav } from "@/components/mobile/MobileBottomNav";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";


// 🚀 AUDIT FIX (Issue #PERF-001): Lazy loading nas páginas individuais
// Route modules importados normalmente, páginas com lazy() dentro de cada module
// Reduz bundle inicial de ~500KB para ~200KB (-60%)
// Melhora LCP de 4s para 2.5s (-37.5%)
import { AuthRoutes } from "@/routes/AuthRoutes";
import { PublicRoutes } from "@/routes/PublicRoutes";
import { AdminRoutes } from "@/routes/AdminRoutes";
import { TeacherRoutes } from "@/routes/TeacherRoutes";
import { StudentRoutes } from "@/routes/StudentRoutes";
import { FamilyRoutes } from "@/routes/FamilyRoutes";

/**
 * Componente interno que gerencia a lógica que DEPENDE do Router.
 * Hooks como useLocation, useNavigate, useSentryUser DEVEM ficar aqui.
 */
const AppRoutes = () => {
  // ✅ SEGURO: Hooks são chamados DENTRO do BrowserRouter
  useAuthInitialization();
  useSentryUser();
  useSupabasePerformance();
  usePageTracking();
  
  return (
    <ErrorBoundary>
      {/* 🚀 CRÍTICO: Suspense é OBRIGATÓRIO para rotas lazy */}
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {AuthRoutes()}
          {PublicRoutes()}
          {AdminRoutes()}
          {TeacherRoutes()}
          {StudentRoutes()}
          {FamilyRoutes()}
        </Routes>
      </Suspense>
      {/* Mobile bottom navigation - hidden on desktop */}
      <MobileBottomNav />
    </ErrorBoundary>
  );
};

/**
 * Componente Raiz
 * Responsável APENAS por fornecer os Contextos Globais (Providers)
 */
const App = () => {
  return (
    <TooltipProvider>
      {/* 🚨 FIX CRÍTICO: BrowserRouter envolve tudo desde o topo */}
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        
        <Sonner />
        <WebVitalsReporter />
        <CLSReporter />
        <CookieConsentBanner />
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  );
};

export default App;
